import {
  GraduationCap,
  CalendarClock,
  Wallet,
  HeartPulse,
  UsersRound,
  Award,
  ArrowRight,
  FileText,
  ShieldCheck,
  Smartphone,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal, FadeIn, Overline } from "@/components/Reveal";

const TEAM_IMG =
  "https://images.pexels.com/photos/6129494/pexels-photo-6129494.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1400";

const benefits = [
  { icon: Wallet, title: "Competitive Pay", text: "Above-market compensation with weekly pay and referral bonuses." },
  { icon: CalendarClock, title: "Flexible Scheduling", text: "Full-time, part-time, per diem, and live-in options that fit your life." },
  { icon: GraduationCap, title: "Training & Growth", text: "Paid orientation, continuing education, and clear paths to advance." },
  { icon: HeartPulse, title: "Supportive Culture", text: "RN supervision and a care team that always has your back in the field." },
  { icon: UsersRound, title: "Meaningful Matches", text: "Thoughtful client matching so your skills and personality shine." },
  { icon: Award, title: "Recognition", text: "Caregiver-of-the-month spotlights and annual appreciation events." },
];

const testimonials = [
  {
    quote: "I've worked for three agencies, and this is the first one that treats caregivers like family. The office truly listens.",
    name: "Denise M.",
    role: "Home Health Aide, 4 years",
  },
  {
    quote: "The flexible scheduling let me finish my nursing degree while working. Now I'm an RN here — they grew with me.",
    name: "Priya S.",
    role: "Registered Nurse",
  },
  {
    quote: "Every client match feels intentional. I get to build real relationships, not just complete shifts.",
    name: "Marcus T.",
    role: "Companion Caregiver",
  },
];

const positions = [
  "Registered Nurse (RN)",
  "Licensed Practical Nurse (LPN)",
  "Home Health Aide (HHA)",
  "Certified Nursing Assistant (CNA)",
  "Companion Caregiver",
  "Other",
];

const Careers = () => {
  return (
    <main>
      <section data-testid="careers-hero" className="relative overflow-hidden pt-40">
        <div className="absolute inset-0">
          <img src={TEAM_IMG} alt="Diverse group of healthcare professionals" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-blush-50/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-white/30" />
        </div>
        <div className="relative mx-auto max-w-7xl px-5 pb-20 sm:px-8">
          <FadeIn><Overline>Careers</Overline></FadeIn>
          <FadeIn delay={0.15}>
            <h1 className="mt-5 max-w-3xl font-serif text-4xl font-medium tracking-tight text-charcoal sm:text-5xl lg:text-6xl">
              Join Our <em className="text-gold-deep">Caring Team</em>
            </h1>
          </FadeIn>
          <FadeIn delay={0.3}>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-charcoal-muted sm:text-lg">
              Build a career where compassion is the job description. We're
              hiring nurses, aides, and caregivers across New Jersey.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="bg-white py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="text-center">
            <Overline centered>Why You'll Love It Here</Overline>
            <h2 className="mt-5 font-serif text-3xl font-medium tracking-tight text-charcoal sm:text-4xl">
              Care for your career, while you care for others
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {benefits.map((b, i) => (
              <Reveal key={b.title} delay={(i % 3) * 0.1}>
                <div className="h-full rounded-3xl border border-blush-200/80 bg-white p-8 shadow-[0_2px_20px_rgba(31,41,55,0.04)] transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:border-gold/40 hover:shadow-[0_16px_40px_rgba(232,180,188,0.22)]">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl border border-gold/30 bg-gold-soft">
                    <b.icon className="h-6 w-6 text-gold" strokeWidth={1.4} />
                  </span>
                  <h3 className="mt-5 font-serif text-xl text-charcoal">{b.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-charcoal-muted">{b.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="career-application-section" className="bg-blush-50 py-20 sm:py-28">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-[1fr_1.2fr]">
          <div>
            <Reveal>
              <Overline>Apply Today</Overline>
              <h2 className="mt-5 font-serif text-3xl font-medium tracking-tight text-charcoal sm:text-4xl">
                Start your application
              </h2>
              <p className="mt-5 max-w-md text-sm leading-relaxed text-charcoal-muted sm:text-base">
                Tell us a little about yourself. Our hiring team reviews every
                application personally and typically responds within two
                business days.
              </p>
            </Reveal>
            <div className="mt-10 space-y-8">
              {testimonials.map((t, i) => (
                <Reveal key={t.name} delay={i * 0.1}>
                  <figure className="rounded-2xl border-l-2 border-gold/50 bg-white/70 p-6 backdrop-blur-sm">
                    <blockquote className="font-serif text-base italic leading-relaxed text-charcoal-light">
                      &ldquo;{t.quote}&rdquo;
                    </blockquote>
                    <figcaption className="mt-3 text-xs uppercase tracking-[0.16em] text-charcoal-faint">
                      {t.name} &middot; <span className="text-gold-deep">{t.role}</span>
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
          </div>

          <Reveal delay={0.1}>
            <div className="rounded-[2rem] border border-blush-200 bg-white p-8 shadow-[0_10px_44px_rgba(31,41,55,0.07)] sm:p-12">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl border border-gold/30 bg-gold-soft">
                <FileText className="h-7 w-7 text-gold" strokeWidth={1.3} />
              </span>
              <h2 className="mt-6 font-serif text-2xl text-charcoal sm:text-3xl">
                Our full employment application is now online
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-charcoal-muted sm:text-base">
                No more downloading or emailing PDFs. Complete the entire application from your
                phone, tablet, or computer — positions, availability, work history, licenses,
                references, and document uploads, all in one guided step-by-step flow.
              </p>
              <div className="mt-7 grid gap-3">
                {[
                  { icon: Smartphone, text: "Mobile-friendly — progress saves automatically" },
                  { icon: ShieldCheck, text: "Secure uploads for your resume and certifications" },
                  { icon: FileText, text: "Electronic signature — no printing required" },
                ].map((f) => (
                  <div key={f.text} className="flex items-center gap-3 text-sm text-charcoal-light">
                    <f.icon className="h-4.5 w-4.5 h-5 w-5 shrink-0 text-gold" strokeWidth={1.5} />
                    {f.text}
                  </div>
                ))}
              </div>
              <Link to="/apply" data-testid="careers-apply-now-main" className="btn-pink mt-9 w-full sm:w-auto">
                Apply Now <ArrowRight className="h-4 w-4" />
              </Link>
              <p className="mt-5 text-xs leading-relaxed text-charcoal-faint">
                Takes about 10 minutes. New Beginnings Healthcare Services is an equal opportunity employer.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Careers;
